/**
 * The Remix state machines (create + edit).
 *
 * Review feedback (Loom #3/#4) asked for:
 *  - the lifecycle to be an *explicit* state machine (events in, state out) using
 *    the same primitive the whiteboard domain uses (the published
 *    `@atlassian/state-machine` package), rather than imperative phase
 *    assignment;
 *  - **two** small machines — one for create, one for edit — because they are
 *    driven by different network streams and, for edits, an extra suppression
 *    rule applies before the first real result.
 *
 * Both machines share the same `RemixPhase` states and the same event
 * vocabulary; they differ in their starting context and (for edit) the presence
 * of the sentinel guard. Keeping them separate means create-only and edit-only
 * behaviour is expressed structurally instead of via `if (kind === ...)`.
 *
 * Cross-store side-effects (editor loading state, opening chat on error) are NOT
 * hard-coded here — they are injected as `onEnter`/`onExit` effects by the
 * store, so this file stays free of editor/chat dependencies. See
 * `./effects/*`.
 */

import { builder as createBuilder } from '@atlassian/state-machine/builder';
import type { Builder, StateBuilder } from '@atlassian/state-machine/builder/types';
import type { Machine, OnEnterEffect, OnExitEffect } from '@atlassian/state-machine/types';

import type { RemixMetadata, RemixPhase } from './remix-instance-types';
import type { RemixEvent, RemixEventLookup } from './remixEvents';

/**
 * The machine's extended state (context). This is the small amount of data the
 * transitions carry; the durable conversation/messages live in the chat store.
 */
export interface RemixMachineContext extends Record<string, unknown> {
	/** The flow's own id (map key in the store). */
	remixId: string;
	/** Whether this flow creates a new remix or edits an existing one. */
	kind: 'create' | 'update';
	/** The conversation backing this flow. */
	conversationId: string;
	/** Durable cancellation session reserved for renderer-owned observation. */
	cancellationSessionId?: string;
	/** What is being remixed (drives UI + payload). */
	metadata: RemixMetadata;
	/** Epoch ms when the flow started — for ordering/debug (was on the snapshot). */
	startedAt: number;
	/**
	 * Identifier of the produced result (mediaFileId / whiteboard contentId /
	 * app id), captured when we entered `ready`.
	 *
	 * This lives on the *action*, so unlike a final-message id it is available at
	 * the exact moment the flow becomes ready. It is stored for debuggability and
	 * as the natural edit sentinel; the result payload itself is still read from
	 * the conversation (`getRemixResultPayload`) rather than copied into state.
	 */
	resultId?: string;
	/**
	 * Edit-only: the *invocation id* of the already-applied result, used to
	 * suppress it until a newer generation streams in. Only present on the edit
	 * machine's context.
	 */
	updateSentinel?: string;
	/** Update-only message count observed before the request was sent. */
	messageCountBeforeSend?: number;
	/**
	 * Anchor node in the document this flow relates to (mediaId / contentMauiId).
	 *
	 * Required for edits — it identifies the existing remix node being replaced.
	 * Optional for creates: an anchored create (e.g. remixing a selected node in
	 * the editor) has one and uses it to target inline/embed loading UI, while an
	 * unanchored create (new content with nowhere to point yet) leaves it unset.
	 */
	targetNodeId?: string;
}

/**
 * One source of injectable cross-store effects. Each hook is optional; the store
 * supplies the real implementations (editor loading, chat open, analytics). Kept
 * as a narrow interface so this module has no editor or chat dependency and is
 * trivially testable with stubs.
 *
 * A machine can be wired with MULTIPLE sources — the factories are variadic and
 * register each source's callbacks separately, so there is no need to compose
 * them into one function. If a single source needs two behaviours for the same
 * state, pass it as two sets.
 */
export interface RemixEffectHooks {
	onEnterThinking?: OnEnterEffect<RemixMachineContext, RemixEvent>;
	onExitThinking?: OnExitEffect<RemixMachineContext, RemixEvent>;
	onEnterReady?: OnEnterEffect<RemixMachineContext, RemixEvent>;
	onEnterError?: OnEnterEffect<RemixMachineContext, RemixEvent>;
	onEnterAborted?: OnEnterEffect<RemixMachineContext, RemixEvent>;
	onEnterInserting?: OnEnterEffect<RemixMachineContext, RemixEvent>;
}

export type RemixMachine = Machine<RemixPhase, RemixEvent, RemixEventLookup, RemixMachineContext>;

type RemixBuilder = Builder<RemixPhase, RemixEvent, RemixEventLookup, RemixMachineContext>;
type RemixStateBuilder = StateBuilder<
	RemixPhase,
	RemixEvent,
	RemixEventLookup,
	RemixMachineContext
>;

/** Identity context update — most transitions carry context forward unchanged. */
const keepContext = (ctx: RemixMachineContext): RemixMachineContext => ctx;

/** Keys of `RemixEffectHooks` that hold an `onEnter` effect. */
type EnterHookKey = Exclude<keyof RemixEffectHooks, 'onExitThinking'>;
/** Keys of `RemixEffectHooks` that hold an `onExit` effect. */
type ExitHookKey = 'onExitThinking';

/**
 * Apply every source's `onEnter`/`onExit` effect for a state to its builder.
 *
 * `.on('enter'|'exit', cb)` ACCUMULATES — the library's effect registrar does
 * `config.effects[name].push(callback)`, and its `EffectLookup` type is an
 * element of `Effects[...]` (an array). So calling it once per source registers
 * them all, in call order, and the machine invokes them in that order.
 *
 * That is why there is no composition helper here: multiple effect sources
 * (ports, command bus, ...) are simply registered one after another rather than
 * being merged into a single callback.
 */
const withEffects = (
	sb: RemixStateBuilder,
	effectSets: readonly RemixEffectHooks[],
	{ enter, exit }: { enter?: EnterHookKey; exit?: ExitHookKey },
): RemixStateBuilder => {
	let next = sb;
	if (enter) {
		for (const set of effectSets) {
			const fn = set[enter];
			if (fn) {
				next = next.on('enter', fn);
			}
		}
	}
	if (exit) {
		for (const set of effectSets) {
			const fn = set[exit];
			if (fn) {
				next = next.on('exit', fn);
			}
		}
	}
	return next;
};

/**
 * Shared state graph used by both machines, expressed via the published
 * `@atlassian/state-machine` builder API (the same primitive canvas uses). The
 * only structural difference between create and edit is the initial context, so
 * the transition topology is defined once here.
 *
 * Transition chain reads: `.when(EVENT, t => t.label(...).if().context(...).to(STATE))`.
 * `.if()` with no args means "no guards"; `.context(fn)` is the context updater.
 */
const buildMachine = (
	context: RemixMachineContext,
	effectSets: readonly RemixEffectHooks[],
): RemixMachine =>
	(createBuilder<RemixPhase, RemixEvent, RemixEventLookup, RemixMachineContext>() as RemixBuilder)
		.initial('thinking')
		.context(context)
		.state('thinking', (s) =>
			withEffects(s, effectSets, { enter: 'onEnterThinking', exit: 'onExitThinking' })
				.when('RESULT_READY', (t) =>
					t
						.label('result is previewable')
						.if()
						.context((ctx, event) => ({ ...ctx, resultId: event.resultId }))
						.to('ready'),
				)
				.when('GENERATION_ERROR', (t) =>
					t.label('generation failed').if().context(keepContext).to('error'),
				)
				.when('GENERATION_ABORTED', (t) =>
					t.label('user aborted').if().context(keepContext).to('aborted'),
				)
				.when('CANCELLED', (t) =>
					t.label('user cancelled').if().context(keepContext).to('aborted'),
				),
		)
		.state('ready', (s) =>
			withEffects(s, effectSets, { enter: 'onEnterReady' })
				.when('DIRECT_INSERTION_STARTED', (t) =>
					t.label('direct insertion started').if().context(keepContext).to('inserting'),
				)
				.when('PREVIEW_OPENED', (t) =>
					t.label('preview shown').if().context(keepContext).to('previewing'),
				)
				.when('GENERATION_ERROR', (t) =>
					t.label('late error').if().context(keepContext).to('error'),
				)
				.when('CANCELLED', (t) =>
					t.label('cancelled before preview').if().context(keepContext).to('aborted'),
				),
		)
		.state('previewing', (s) =>
			s
				.when('ACCEPTED', (t) =>
					t.label('user accepted result').if().context(keepContext).to('inserting'),
				)
				.when('CANCELLED', (t) =>
					t.label('user discarded preview').if().context(keepContext).to('aborted'),
				)
				.when('GENERATION_ERROR', (t) =>
					t.label('error during preview').if().context(keepContext).to('error'),
				)
				.when('INSERTION_FAILED', (t) =>
					t.label('preview insertion failed').if().context(keepContext).to('error'),
				),
		)
		.state('inserting', (s) =>
			// `inserting` is terminal from the machine's perspective: once INSERTED
			// fires the store removes the instance. Modelled as the `done` command.
			withEffects(s, effectSets, { enter: 'onEnterInserting' })
				.when('INSERTED', (t) => t.label('insertion complete').if().context(keepContext).to('done'))
				.when('GENERATION_ERROR', (t) =>
					t.label('insertion failed').if().context(keepContext).to('error'),
				)
				.when('INSERTION_FAILED', (t) =>
					t.label('insertion failed').if().context(keepContext).to('error'),
				),
		)
		.state('error', (s) => withEffects(s, effectSets, { enter: 'onEnterError' }))
		.state('aborted', (s) => withEffects(s, effectSets, { enter: 'onEnterAborted' }))
		.build();

/**
 * Context for a create flow: the base fields, with the edit-only sentinel
 * explicitly absent.
 *
 * `targetNodeId` is deliberately NOT omitted — a create may be anchored to an
 * existing node (remixing a selection in the editor), which is what the
 * inline/embed loading UI targets. It stays optional here and is narrowed to
 * required on `RemixEditContext`.
 */
export type RemixCreateContext = Omit<RemixMachineContext, 'updateSentinel'>;

/**
 * Create machine: a brand-new remix. Starts in `thinking` with no sentinel.
 *
 * The caller passes only the base context; edit-only fields are left undefined
 * on the underlying `RemixMachineContext`.
 */
export const createRemixCreateMachine = (
	context: RemixCreateContext,
	...effectSets: RemixEffectHooks[]
): RemixMachine => buildMachine({ ...context } as RemixMachineContext, effectSets);

/**
 * Edit machine: editing an already-inserted remix. Starts in `thinking` and
 * carries the `updateSentinel` + `targetNodeId` in context so the
 * conversation translator can suppress the previously-applied result and the
 * effects can target the right node.
 *
 * Structurally identical topology to create, but a distinct machine (and
 * distinct context requirements) per the review's "two tiny machines" guidance —
 * they are fed by different network streams.
 */
export type RemixEditContext = RemixCreateContext & {
	updateSentinel: string;
	targetNodeId: string;
	messageCountBeforeSend?: number;
};

export const createRemixEditMachine = (
	context: RemixEditContext,
	...effectSets: RemixEffectHooks[]
): RemixMachine => buildMachine({ ...context } as RemixMachineContext, effectSets);
